import React, { InputHTMLAttributes, forwardRef, useId } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, icon, className = '', style, ...props }, ref) => {
    const { isDark } = useTheme();
    const generatedId = useId();
    const inputId = props.id || generatedId;
    const errorId = `${inputId}-error`;

    return (
      <div style={{ width: '100%' }}>
        {label && (
          <label htmlFor={inputId} style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: isDark ? '#D1D5DB' : '#374151',
            marginBottom: '4px',
          }}>
            {label}
          </label>
        )}
        <div style={{ position: 'relative' }}>
          {icon && (
            <div style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              paddingLeft: '12px',
              display: 'flex',
              alignItems: 'center',
              pointerEvents: 'none',
              color: '#9CA3AF',
            }}>
              {icon}
            </div>
          )}
          <input
            ref={ref}
            className={`ui-input ${isDark ? 'ui-input-dark' : ''} ${className}`}
            id={inputId}
            aria-label={props['aria-label'] || (!label ? props.placeholder : undefined)}
            aria-invalid={!!error || undefined}
            aria-describedby={error ? errorId : props['aria-describedby']}
            style={{
              width: '100%',
              colorScheme: isDark ? 'dark' : 'light',
              padding: '10px 16px',
              paddingLeft: icon ? '40px' : '16px',
              borderRadius: '8px',
              border: `1px solid ${error ? (isDark ? '#FCA5A5' : '#B91C1C') : isDark ? '#4B5563' : '#D1D5DB'}`,
              backgroundColor: isDark ? '#374151' : '#FFFFFF',
              color: isDark ? '#F9FAFB' : '#111827',
              fontSize: '14px',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              ...style,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = error ? '#EF4444' : '#2563EB';
              e.target.style.boxShadow = `0 0 0 2px ${error ? 'rgba(239,68,68,0.2)' : 'rgba(37,99,235,0.2)'}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error ? '#EF4444' : isDark ? '#4B5563' : '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }}
            {...props}
          />
        </div>
        {error && (
          <p id={errorId} style={{ marginTop: '4px', fontSize: '14px', color: isDark ? '#FCA5A5' : '#B91C1C' }}>{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
